import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { OnboardingModuleStatuses } from 'src/app/core/enums/onboarding-module-status.enum';
import { IMultiOptions } from 'src/app/core/interfaces/onboarding/business-profile/business-multi-options.interface';
import { ICustomerPortfolio } from 'src/app/core/interfaces/onboarding/customer-portfolio/customer-portfolio.interface';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { CustomerPortfolioService } from 'src/app/core/services/onboarding/customer-portfolio.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { selectAllBusinessProfile } from 'src/app/core/store/onboarding/reducers+selectors/business-profile.selector';
import { selectCustomerDetails } from 'src/app/core/store/onboarding/reducers+selectors/customer-portfolio.selector';
import { selectModuleStatuses } from 'src/app/core/store/onboarding/reducers+selectors/enrollment.selector';
import { selectAllFinancialProfile } from 'src/app/core/store/onboarding/reducers+selectors/financial-profile.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { syncComponentHeightInterval } from 'src/app/core/ui-helpers/functions/sync-component-height';
import { SidenavComponent } from '../sidenav/sidenav.component';

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() headerText: string;
  @Input() homeState: PageState;
  @Input() bpState: PageState;
  @Input() fpState: PageState;
  @Input() cpState: PageState;
  @Input() disableDocuments = false;
  @Input() progressIncrement: number;
  isMobileOpen = false;
  isAccountBubbleOpen = false;
  isContactOpen = false;
  @ViewChild('content', { static: true }) content: ElementRef<HTMLDivElement>;
  @ViewChild('container', { static: true })
  container: ElementRef<HTMLDivElement>;
  @ViewChild(SidenavComponent) sidenavComponent: SidenavComponent;
  @Input() needsProgressBars = true;
  bpStepsCompleted = 0;
  bpStepsTotal = 12;
  fpStepsCompleted = 0;
  fpStepsTotal = 7;
  cpStepsCompleted = 1;
  cpStepsTotal = 11;
  subscriptions: Subscription[] = [];
  custSelect = 0;
  payload: ICustomerPortfolio;
  interval: ReturnType<typeof setInterval>;

  constructor(
    private store: Store<IAppState>,
    private cp: CustomerPortfolioService
  ) {}

  ngOnInit(): void {
    if (this.progressIncrement) {
      this.custSelect = this.progressIncrement;
    }
    this.payload = this.cp.getPayload();
    this.populateProgressBars();
  }

  ngAfterViewInit(): void {
    this.interval = syncComponentHeightInterval(
      this.sidenavComponent,
      this.content,
      this.container
    );
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
    clearInterval(this.interval);
  }

  openMobile() {
    this.isMobileOpen = !this.isMobileOpen;
  }

  closeMobile() {
    if (this.isMobileOpen) this.isMobileOpen = !this.isMobileOpen;
  }

  populateProgressBars() {
    const moduleStatusProps$: Observable<any> =
      this.store.select(selectModuleStatuses);

    this.subscriptions.push(
      moduleStatusProps$.subscribe((props) => {
        if (
          props.businessProfileStatusId === OnboardingModuleStatuses.COMPLETED
        ) {
          this.bpStepsCompleted = this.bpStepsTotal;
        } else if (
          props.businessProfileStatusId === OnboardingModuleStatuses.INACTIVE
        ) {
          this.bpStepsCompleted = 0;
        } else {
          this.populateBpProgressBar();
        }

        if (
          props.financialProfileStatusId === OnboardingModuleStatuses.COMPLETED
        ) {
          this.fpStepsCompleted = this.fpStepsTotal;
        } else if (
          props.financialProfileStatusId === OnboardingModuleStatuses.INACTIVE
        ) {
          this.fpStepsCompleted = 0;
        } else {
          this.populateFpProgressBar();
        }

        if (
          props.customerPortfolioStatusId === OnboardingModuleStatuses.COMPLETED
        ) {
          this.cpStepsCompleted = this.cpStepsTotal;
        } else if (
          props.customerPortfolioStatusId === OnboardingModuleStatuses.INACTIVE
        ) {
          this.cpStepsCompleted = 0;
        } else {
          this.populateCpProgressBar();
        }
      })
    );
  }

  populateCpProgressBar() {
    const cpProps$: Observable<any> = this.store.select(selectCustomerDetails);
    this.subscriptions.push(
      cpProps$.subscribe((props) => {
        this.cpStepsCompleted += this.custSelect;
        if (props.customerName) {
          this.cpStepsCompleted += 3;
        }
        if (props.isFederal || props.isFederal === false) {
          this.cpStepsCompleted += 1;
        }
        if (props.billingAddress.address1 !== '') {
          this.cpStepsCompleted += 1;
        }
        if (
          props.isHeadquarterAddress ||
          props.isHeadquarterAddress === false
        ) {
          this.cpStepsCompleted += 1;
        }
        if (props.headquarterAddress.address1 !== '') {
          this.cpStepsCompleted += 1;
        }

        if (props.invoiceAmount !== '') {
          this.cpStepsCompleted += 1;
        }

        if (props.invoiceTerm.value !== '') {
          this.cpStepsCompleted += 2;
        }

        if (this.payload.customerId) {
        }
      })
    );
    if (this.cpStepsCompleted >= 11) {
      this.cpStepsCompleted = 11;
    }
  }

  populateBpProgressBar() {
    const bpProps$: Observable<any> = this.store.select(
      selectAllBusinessProfile
    );
    this.subscriptions.push(
      bpProps$.subscribe((props) => {
        if (props.businessType?.value) this.bpStepsCompleted += 1;
        if (props.businessIndustry?.value) this.bpStepsCompleted += 1;
        if (props.customerTypes?.some((x: IMultiOptions) => x.selected))
          this.bpStepsCompleted += 1;
        if (props.stateOfOrg?.value) this.bpStepsCompleted += 1;
        /* if (props.dba) */ this.bpStepsCompleted += 1;
        if (props.companyAddress?.address1) this.bpStepsCompleted += 1;
        if (props.annualRevenue?.value) this.bpStepsCompleted += 1;
        /* if (props.companyWebsite) */ this.bpStepsCompleted += 1;
        if (props.yearFounded) this.bpStepsCompleted += 1;
        if (props.employeeCount?.value) this.bpStepsCompleted += 1;
        if (props.invoicingMethod?.value) this.bpStepsCompleted += 1;
        if (props.supportMethod?.some((x: IMultiOptions) => x.selected))
          this.bpStepsCompleted += 1;
      })
    );
  }

  populateFpProgressBar() {
    const fpProps$: Observable<any> = this.store.select(
      selectAllFinancialProfile
    );
    this.subscriptions.push(
      fpProps$.subscribe((props) => {
        if (
          props.invoiceAmount?.id !== -1 ||
          (props.invoiceAmount?.value !== '' &&
            props.invoiceAmount?.value !== '0')
        )
          this.fpStepsCompleted += 1;
        if (props.invoiceTerm?.id !== -1 || props.invoiceTerm?.value !== '')
          this.fpStepsCompleted += 1;
        if (props.bankStatementsComplete) this.fpStepsCompleted += 1;
        if (props.balanceSheetsComplete) this.fpStepsCompleted += 1;
        if (props.plStatementsComplete) this.fpStepsCompleted += 1;
        if (props.arAgingComplete) this.fpStepsCompleted += 1;
        if (props.apAgingComplete) this.fpStepsCompleted += 1;
      })
    );
  }
}
